import React from 'react';
import {Button, Col, Container, Row} from 'reactstrap';
import ReactMarkdown from 'react-markdown';
import useBalanceText from 'utils/useBalanceText';
import PageMeta from 'components/PageMeta';
import {SectionTriangle} from 'components/Elements/Section';
import Video from 'components/Video';
import {ImgCard} from 'components/Elements/ImgBullet';
import {FaqCard} from 'components/FaqList';
import {SalesDisclaimer} from 'components/SalesCta';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';
import {conversations, impactKit, experts, extendedInterviews} from 'data/fungi.json';

export default function SalesIndex({
  noExitIntent = false,
  noFixedVideo = true,
  ctaOverride = null,
  videoUrlOverride = '',
  ctaNoTimer = false,
  variant = null,
  pageTitle = 'Fantastic Fungi - Impact Kit',
  pageDescription = 'page description',
  shareUrl = 'https://fungi.foodrevolution.org/',
  ogImage = 'https://cdn.foodrevolution.org/global/images/placeholder.jpg',
  facebookDescription = '',
  twitterDescription = '',
  pinterestDescription = '',
  pinterestImg = '',
  ...props
}) {
  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />

      <div className="page-content">
        <SectionTriangle
          id="ff-up-header-1"
          color="black"
          className="section-triangle-first"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-header-backdrop-landon-parenteau-EEuDMqRYbx0-unsplash.jpg')}
          innerClass="p-0"
        />

        <SectionTriangle id="ff-up-header-2" color="white" type="both" className="text-center">
          <Container>
            <div className="offset-box background-white-80 border-ff-gray-10-solid p-3 p-md-4 p-lg-5">
              <Row>
                <Col>
                  <h3 className="section-heading mb-3">
                    Thank you for choosing to own <i>Fantastic Fungi!</i>
                  </h3>
                  <p className="balance-text mb-4">
                    Now watch this short video to see how you can take the next step for an unbeatable price you’ll
                    never see again.
                  </p>
                  <Video url="https://www.youtube.com/watch?v=8qMrptXMmgg" label="FF - Upsell" />
                </Col>
              </Row>
              <Row className="offset-box-cta">
                <Col>
                  <p className="balance-text">
                    I wanted to make sure you didn’t miss this one-time offer to join us today as we change the world
                    together.
                  </p>
                  <h5 className="text-400 m-0">
                    <i>
                      Are you ready to discover more of the mysteries of mycelium, delve into the world of plant
                      medicine, and enjoy the satisfaction of building a better future for life on Earth?
                    </i>
                  </h5>
                </Col>
              </Row>
            </div>
            <h5>
              <i>One-time offer — 69% savings!</i>
            </h5>
            <p>
              <b>Support Your Health and Our Mission And Get Immediate Lifetime Access to the Impact Kit Today</b>
            </p>
            <Button color="cta-ff" href="#" className="mb-3">
              YES, I WANT TO ADD THIS TO MY ORDER
            </Button>
            <p>Clicking "Yes" above will add The Impact Kit to your purchase now for an additional $60.</p>
            <p>Your purchase comes with a risk-free, 60-day, total money-back guarantee.</p>
            <Button color="upsell-gray" href="#" className="mb-3">
              No, thanks
            </Button>
            <p>Clicking the "No" button above will take you to the products already in your cart.</p>
            <SalesDisclaimer noGuaranteeText />
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="ff-"
          color="black"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-body-voices-marc-markstein-ibouKITXBFM-unsplash.jpg')}
          innerClass="d-flex flex-grow-1"
          offset="bottom"
          className="background-overlay-black-50 text-white text-center text-shadow-dark"
        >
          <Container>
            <Row className="justify-content-center">
              <Col lg="10">
                <h3>
                  We spent years pouring our hearts and souls into telling the important story captured in{' '}
                  <i>Fantastic Fungi.</i> We hope the film inspired you.
                </h3>
                <h4>
                  Now’s the time to dig deeper with the <i>Fantastic Fungi Impact Kit</i> — brought to you by Louie and
                  the <i>Fantastic Fungi</i> team, in partnership with Food Revolution Network
                </h4>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="ff-cream" type="both">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center">
                  Unlock the Power of Mushrooms with the Experts Featured in <i>Fantastic Fungi</i>
                </h3>
                <p className="text-center balance-text mb-5">
                  The film was just the beginning! Get over 16 hours of new insights into the mushroom kingdom, plus
                  never-before-released bonus footage from <i>Fantastic Fungi.</i>
                </p>
                <ImgCard img={require('static/backgrounds/151812309-wide.jpg')} className="text-h5 mb-4">
                  <b>Revitalize</b> your body to fight disease and promote vibrant health with potent nutrient-dense
                  mushrooms.
                </ImgCard>
                <ImgCard img={require('static/backgrounds/1316793260-wide.jpg')} className="text-h5 mb-4">
                  <b>Discover</b> the latest medical research in the treatment of depression, anxiety addiction, PTSD,
                  end-of-life care, and more.
                </ImgCard>
                <ImgCard img={require('static/backgrounds/904902642-wide.jpg')} className="text-h5 mb-4">
                  <b>Gain</b> insights into psychedelic uses of mushrooms — knowledge that every adult should have right
                  now!
                </ImgCard>
                <ImgCard img={require('static/backgrounds/1028900652-wide.jpg')} className="text-h5 mb-4">
                  <b>Deepen</b> connection to the natural world and to the humanity that we all have in common.
                </ImgCard>
                <ImgCard img={require('static/backgrounds/489150096-wide.jpg')} className="text-h5 mb-4">
                  <b>Demystify</b> the often-misunderstood world of mushrooms{' '}
                </ImgCard>
                <ImgCard img={require('static/backgrounds/1270104024-wide.jpg')} className="text-h5">
                  <b>Protect</b> yourself and your loved ones from a future of environmental destruction.
                </ImgCard>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="ff-gray" type="bottom" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading">Are you ready to join this world-changing revolution?</h4>
                <h3 className="mt-0">
                  The <i>Fantastic Fungi Impact Kit</i> will show you how.
                </h3>
                <p className="balance-text">
                  You’ll have 20+ master impact leaders and teachers showing you the simplest ways to be part of the
                  solution on planet Earth.
                </p>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="ff-cream" type="bottom">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center">
                  What’s Included In The <i>Fantastic Fungi Impact Kit</i>
                </h3>
              </Col>
            </Row>
            <Row className="d-flex align-items-stretch">
              {impactKit.map((impactKitItem, i) => (
                <Col lg="6" className="d-flex flex-column mb-4">
                  <div className="background-white box-shadow-black-25 d-flex flex-column flex-grow-1">
                    <div className="impact-kit-image mb-3">
                      <img src={impactKitItem.image} />
                    </div>
                    <div className="p-4">
                      <ReactMarkdown className="text-h5 text-800" source={impactKitItem.name} />
                      <ReactMarkdown source={impactKitItem.text} />
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
            <Row>
              <Col>
                <h3 className="text-center mt-4">
                  You can’t see all these Masterclasses and extended bonuses anywhere else. You can only get it all
                  here, now!
                </h3>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="ff-gray" type="bottom">
          <Container>
            <Row>
              <Col>
                <h2 className="section-only-heading text-center text-white">
                  Deepen Your Learning With <i>Fantastic Fungi’s</i> Experts
                </h2>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="white" type="bottom">
          <Container>
            <Row className="d-flex align-items-stretch justify-content-center text-center">
              {experts.map((expert, i) => (
                <Col xs="6" lg="4" className="expert-wrap d-flex flex-column px-3 mb-4">
                  <div className="mb-3">
                    <img className="rounded-circle" src={expert.image} style={{maxHeight: '150px'}} />
                  </div>
                  <div className="my-auto">
                    <h5 className="section-heading mb-2">{expert.name}</h5>
                    <ReactMarkdown className="text-small" source={expert.description} />
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="ff-gray" type="bottom" className="text-white text-center">
          <Container>
            <Row>
              <Col>
                <h2 className="section-only-heading">Does any of this sound like you?</h2>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id=""
          className="section-triangle-image-lg-none"
          color="white"
          type="bottom"
          imgUrl={require('static/backgrounds/1283900775.jpg')}
          imgPosition="left"
        >
          <h3 className="section-heading">You love health.</h3>
          <p>
            Science is showing us how certain mushrooms are renowned for their health benefits and could provide more
            energy, immune health, relaxation, and great sleep.
          </p>
        </SectionTriangle>

        <SectionTriangle
          id=""
          className="section-triangle-image-lg-none section-triangle-image-lg-none-offset"
          color="white"
          type="bottom"
          imgUrl={require('static/backgrounds/151812309.jpg')}
          imgPosition="right"
        >
          <h3 className="section-heading">You want to feel good and free.</h3>
          <p>
            You’ve considered or even tried healing routes like therapy, self-help books, journaling, and possibly
            medication. And you want to know everything you can about how people are successfully using therapeutic
            mushrooms to experience healing breakthroughs. Are mushrooms for everyone? What do you need to know about
            this new and exciting healing modality?
          </p>
        </SectionTriangle>
        <SectionTriangle
          id=""
          className="section-triangle-image-lg-none section-triangle-image-lg-none-offset"
          color="white"
          type="bottom"
          imgUrl={require('static/backgrounds/489150096.jpg')}
          imgPosition="left"
        >
          <h3 className="section-heading">
            You want reliable research on how to use “magic” mushrooms with healing intent.
          </h3>
          <p>
            In the last few years, research and major news features are praising psychoactive forms of mushrooms for
            everything from addiction treatment to spiritual connection to microdosing for better brain performance.
            What are the experts saying? And how can we harness the latest scientific research to expand our collective
            consciousness and heal — safely?
          </p>
        </SectionTriangle>
        <SectionTriangle
          id=""
          className="section-triangle-image-lg-none section-triangle-image-lg-none-offset"
          color="white"
          type="bottom"
          imgUrl={require('static/backgrounds/878854774.jpg')}
          imgPosition="right"
        >
          <h3 className="section-heading">Climate change matters to you.</h3>
          <p>
            Fungi are being hailed as the greatest natural solution to climate change. They add carbon to the soil,
            filter water, provide nutrients to plants that pump out oxygen, clean up oil spills and pollution, and
            provide 100% compostable sources of packaging. You want to know how mushrooms can help make the world a
            better place.
          </p>
        </SectionTriangle>
        <SectionTriangle
          id=""
          className="section-triangle-image-lg-none section-triangle-image-lg-none-offset"
          color="white"
          type="bottom"
          imgUrl={require('static/backgrounds/mushroom-stew.jpg')}
          imgPosition="left"
        >
          <h3 className="section-heading">You’re a bit of a foodie!</h3>

          <p>
            You love wild food, foraging, cooking new dishes, and most of all, eating! Whether it’s adding a few
            supporting mushrooms for a lip-smacking umami experience, or making them the star of the show, you’re
            excited to lean into mushrooms for all their culinary delight.
          </p>
        </SectionTriangle>
        <SectionTriangle
          id=""
          className="section-triangle-image-lg-none-offset"
          color="white"
          type="bottom"
          imgUrl={require('static/backgrounds/1301815523.jpg')}
          imgPosition="right"
        >
          <h3 className="section-heading">You’re interested in eating a more plant-centered diet.</h3>
          <p>
            Mushrooms are one of the most savory ways to do just that — and you can enjoy the outstanding health
            benefits while contributing to significantly fewer greenhouse gas emissions.
          </p>
        </SectionTriangle>

        <SectionTriangle
          id="ff-up-unlock"
          color="black"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-body-joinrevolution-jesse-dodds-ZjDhntvNzAs-unsplash.jpg')}
          innerClass="d-flex flex-grow-1"
          offset="bottom"
          className="background-overlay-black-50 text-center text-white text-shadow-dark"
        >
          <Container>
            <Row>
              <Col>
                <h2 className="section-only-heading">
                  Unlock the Power of Mushrooms — for your health, for your happiness, and for your planet
                </h2>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="ff-gray" type="both">
          <Container>
            <Row>
              <Col>
                <h2 className="section-only-heading text-center text-white">Unreleased & Extended Interviews</h2>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="ff-cream" type="bottom">
          <Container>
            <Row>
              <Col>
                <p className="text-center mb-5">
                  There’s only one way to get access to these additional interviews with the featured experts, deleted
                  scenes, and bonus features: The <i>Fantastic Fungi Impact Kit</i>
                </p>
              </Col>
            </Row>
            <Row className="d-flex align-items-stretch justify-content-center">
              {extendedInterviews.map((interview, i) => (
                <Col xs="6" lg="3" className="interview-wrap d-flex flex-grow-1 px-3 mb-4 mb-lg-0">
                  <div className="d-flex flex-column background-white box-shadow-black-25">
                    <div className="px-1 pt-1 pb-0">
                      <img src={interview.image} />
                    </div>
                    <div className="p-3 my-auto">
                      <p className="text-small text-800 mb-1">{interview.name}</p>
                      <p className="text-small mb-0">{interview.type}</p>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="ff-trees"
          color="white"
          imgUrl={require('static/backgrounds/1322528652.jpg')}
          imgPosition="right"
          type="bottom"
          className="text-center text-lg-left"
        >
          <h4 className="section-heading">Want a better world? We do, too!</h4>
          <p className="text-h5">
            For every Impact Kit purchased, we make a donation to Trees for the Future, enabling them to plant another{' '}
            organic fruit or nut tree in a <br className="d-none d-xl-block" />
            low-income community.
          </p>
          <img src="https://cdn.foodrevolution.org/global/trees-logo-green.png" style={{maxHeight: '100px'}} />
        </SectionTriangle>

        <SectionTriangle
          id="ff-need-2"
          color="black"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-body-needyou-karl-anderson-KH-WE85l3kc-unsplash.jpg')}
          bgImgPosition="center-bottom"
          offset="bottom"
        />

        <SectionTriangle id="" color="ff-cream" type="both">
          <Container>
            <Row>
              <Col lg="4">
                <h3 className="section-heading text-center text-lg-right">Frequently Asked Questions</h3>
              </Col>
              <Col lg="8">
                <FaqCard />
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="white" type="bottom" className="text-center">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">
                  You’ve decided to own <i>Fantastic Fungi.</i>
                </h3>
                <h4 className="mt-0">
                  Now make the film have <i>real impact</i> by adding the Impact Kit to your order.
                </h4>

                <img className="mb-5" src="https://cdn.foodrevolution.org/ff/FF-Product Image-v20220527.png" />
                <p className="balance-text">
                  Once you close this page, you’ll miss this exclusive, majorly discounted offer for the{' '}
                  <b>
                    <i>Fantastic Fungi Impact Kit.</i>
                  </b>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="ff-cta-1"
          color="black"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-header-backdrop-landon-parenteau-EEuDMqRYbx0-unsplash.jpg')}
        >
          <Container>
            <Row className="justify-content-center">
              <Col lg="10" xl="8">
                <div className="cta-box border-ff-orange-5-solid text-center">
                  <SectionTriangle id="" color="white" type="bottom" innerClass="pt-4 pt-lg-5">
                    <h5 className="section-only-heading">
                      <i>One-Time Offer Only!</i>
                    </h5>
                  </SectionTriangle>
                  <SectionTriangle id="" color="ff-cream" type="bottom">
                    <Button color="cta-ff" href="#" className="mb-3">
                      YES, I WANT TO ADD THIS TO MY ORDER
                    </Button>
                    <p>
                      Clicking "Yes" above will add The Impact Kit <br className="d-none d-xl-block" />
                      to your purchase now for an additional $60.
                    </p>
                    <p>
                      Your purchase comes with a risk-free, <br className="d-none d-xl-block" />
                      60-day, total money-back guarantee.
                    </p>
                    <Button color="upsell-gray" href="#" className="mb-3">
                      No, thanks
                    </Button>
                    <p>
                      Clicking the "No" button above will take you <br className="d-none d-xl-block" />
                      to the products already in your cart.
                    </p>
                  </SectionTriangle>
                  <SectionTriangle id="" color="white">
                    <SalesDisclaimer className="mt-0" noGuaranteeText />
                  </SectionTriangle>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>
      </div>

      <div className="footer-content mt-auto">
        <Footer partner />
        <ShareButtons
          shareUrl={shareUrl}
          shareImg={ogImage}
          facebookDescription={facebookDescription}
          twitterDescription={twitterDescription}
          pinterestDescription={pinterestDescription}
          pinterestImg={pinterestImg}
          floating
        />
      </div>
    </div>
  );
}
